import { useState, useCallback } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Search, Plus, Trash2, X } from 'lucide-react';
import { vendorsApi, type VendorsParams, type TenantSearchResult } from '../../api/endpoints/vendors';
import { useDebounce } from '../../hooks/useDebounce';
import { queryClient } from '../../providers/QueryProvider';
import { StatCard } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Table, Thead, Th, Td, Tr } from '../../components/ui/Table';
import { Pagination } from '../../components/ui/Pagination';
import { Modal } from '../../components/ui/Modal';
import { PageSpinner } from '../../components/ui/Spinner';
import { formatTimeAgo } from '../../utils/formatters';

export function VendorsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const debouncedSearch = useDebounce(search);

  const params: VendorsParams = { page, limit: 20, search: debouncedSearch };

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['vendors', params],
    queryFn: () => vendorsApi.list(params),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => vendorsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
  });

  const vendors = data?.vendors || [];
  const totalPages = data?.totalPages || 1;

  // Stats derived from current list
  const stats = {
    total: data?.total || 0,
    active: vendors.filter(v => v.status === 'active').length,
    devices: vendors.reduce((s, v) => s + (v.device_count || 0), 0),
  };

  const handleDelete = useCallback((e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (confirm('Delete this vendor? This cannot be undone.')) {
      deleteMutation.mutate(id);
    }
  }, [deleteMutation]);

  if (isLoading && !data) return <PageSpinner />;

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-lg font-semibold text-gray-800 dark:text-white">Vendor Management</h1>
          <p className="text-gray-500 dark:text-gray-400 text-xs mt-0.5">Manage vendor portal accounts and tenant assignments</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => refetch()} className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-1">
            <RefreshCw size={14} />
            Refresh
          </button>
          <button onClick={() => setShowCreate(true)} className="bg-purple-600 text-white px-3 py-1.5 rounded-md hover:bg-purple-700 transition-colors flex items-center gap-1 text-xs">
            <Plus size={14} />
            Add Vendor
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <StatCard label="Total Vendors" value={stats.total} color="purple" />
        <StatCard label="Active" value={stats.active} color="green" />
        <StatCard label="Total Devices" value={stats.devices} color="blue" />
      </div>

      {/* Search */}
      <div className="bg-white dark:bg-[#111] rounded-md shadow-sm border border-gray-200 dark:border-gray-800 p-3 mb-4">
        <div className="relative max-w-xs">
          <Search size={16} className="absolute left-2.5 top-2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search vendors..."
            className="w-full pl-8 pr-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-xs dark:bg-gray-700 dark:text-white"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-[#111] rounded-lg shadow-sm border border-gray-200 dark:border-gray-800">
        <Table>
          <Thead>
            <tr>
              <Th>Vendor</Th>
              <Th>Username</Th>
              <Th>Tenant</Th>
              <Th>Subdomain</Th>
              <Th>Max Screens</Th>
              <Th>Devices</Th>
              <Th>Status</Th>
              <Th>Last Login</Th>
              <Th>Actions</Th>
            </tr>
          </Thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
            {vendors.map(vendor => {
              const statusVariant = vendor.status === 'active' ? 'green' : vendor.status === 'suspended' ? 'red' : 'yellow';
              return (
                <Tr key={vendor._id} onClick={() => navigate(`/vendors/${vendor._id}`)}>
                  <Td>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                        <span className="text-purple-600 dark:text-purple-400 font-bold text-xs">{vendor.name.charAt(0).toUpperCase()}</span>
                      </div>
                      <div>
                        <span className="font-medium text-purple-600 dark:text-purple-400">{vendor.name}</span>
                        <p className="text-[10px] text-gray-400">{vendor.email || 'No email'}</p>
                      </div>
                    </div>
                  </Td>
                  <Td>
                    <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded font-mono">{vendor.username}</code>
                  </Td>
                  <Td>{vendor.tenant_name || vendor.tenant_id}</Td>
                  <Td>{vendor.subdomain || <span className="text-gray-400">--</span>}</Td>
                  <Td>{vendor.settings?.max_screens || 1}</Td>
                  <Td>
                    <span className="font-medium">{vendor.device_count || 0}</span>
                    <span className="text-gray-400 ml-1">({vendor.connected_count || 0} online)</span>
                  </Td>
                  <Td><Badge variant={statusVariant} dot>{vendor.status}</Badge></Td>
                  <Td>{formatTimeAgo(vendor.last_login || '')}</Td>
                  <Td>
                    <button
                      onClick={e => handleDelete(e, vendor._id)}
                      className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-xs font-medium"
                    >
                      <Trash2 size={14} />
                    </button>
                  </Td>
                </Tr>
              );
            })}
            {vendors.length === 0 && (
              <tr><td colSpan={9} className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">No vendors found</td></tr>
            )}
          </tbody>
        </Table>
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />

      {/* Create Modal */}
      <CreateVendorModal open={showCreate} onClose={() => setShowCreate(false)} />
    </div>
  );
}

// --- Create Vendor Modal ---

function CreateVendorModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [maxScreens, setMaxScreens] = useState(1);
  const [sessionTtl, setSessionTtl] = useState(1);
  const [error, setError] = useState('');

  // Tenant search state
  const [tenantQuery, setTenantQuery] = useState('');
  const [tenantResults, setTenantResults] = useState<TenantSearchResult[]>([]);
  const [tenantSearching, setTenantSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<{ tenant_id: string; tenant_name: string; subdomain: string } | null>(null);

  const createMutation = useMutation({
    mutationFn: () =>
      vendorsApi.create({
        name,
        username,
        password,
        email: email || undefined,
        phone: phone || undefined,
        tenant_id: selectedTenant!.tenant_id,
        tenant_name: selectedTenant!.tenant_name || undefined,
        subdomain: selectedTenant!.subdomain || undefined,
        settings: { max_screens: maxScreens, session_ttl_days: sessionTtl },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      resetForm();
      onClose();
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const resetForm = () => {
    setName('');
    setUsername('');
    setPassword('');
    setEmail('');
    setPhone('');
    setMaxScreens(1);
    setSessionTtl(1);
    setTenantQuery('');
    setTenantResults([]);
    setSelectedTenant(null);
    setError('');
  };

  const handleSearchTenants = async () => {
    if (tenantQuery.length < 2) return;
    setTenantSearching(true);
    try {
      const data = await vendorsApi.searchTenants(tenantQuery);
      setTenantResults(data.tenants || []);
      setShowResults(true);
    } catch {
      setTenantResults([]);
    } finally {
      setTenantSearching(false);
    }
  };

  const handleSelectTenant = (t: TenantSearchResult) => {
    setSelectedTenant({
      tenant_id: t._id || t.id || t.tenant_id || '',
      tenant_name: t.name || t.tenant_name || '',
      subdomain: t.subdomain || '',
    });
    setShowResults(false);
    setTenantQuery(t.subdomain || t.name || t.tenant_name || '');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!selectedTenant) {
      setError('Please search and select a tenant first');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    createMutation.mutate();
  };

  const inputClass = 'w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs focus:ring-1 focus:ring-purple-500 dark:bg-gray-700 dark:text-white';
  const labelClass = 'block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1';

  return (
    <Modal open={open} onClose={() => { resetForm(); onClose(); }} title="Add Vendor" wide>
      {error && (
        <div className="mb-3 p-2 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-xs text-red-700 dark:text-red-400">
          {error}
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className={labelClass}>Vendor Name *</label>
          <input type="text" value={name} onChange={e => setName(e.target.value)} required className={inputClass} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Username *</label>
            <input type="text" value={username} onChange={e => setUsername(e.target.value)} required className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Password *</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} className={inputClass} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Phone</label>
            <input type="text" value={phone} onChange={e => setPhone(e.target.value)} className={inputClass} />
          </div>
        </div>

        {/* Tenant Search */}
        <div>
          <label className={labelClass}>Tenant (Search by subdomain) *</label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={tenantQuery}
              onChange={e => setTenantQuery(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSearchTenants(); } }}
              placeholder="Type subdomain to search..."
              className={`flex-1 ${inputClass}`}
            />
            <button
              type="button"
              onClick={handleSearchTenants}
              disabled={tenantSearching || tenantQuery.length < 2}
              className="bg-purple-600 text-white px-3 py-1.5 rounded-md hover:bg-purple-700 text-xs font-medium whitespace-nowrap disabled:opacity-50"
            >
              {tenantSearching ? 'Searching...' : 'Search'}
            </button>
          </div>

          {/* Search results dropdown */}
          {showResults && tenantResults.length > 0 && (
            <div className="mt-1 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 shadow-sm">
              {tenantResults.map((t, i) => {
                const tid = t._id || t.id || t.tenant_id || '';
                const tname = t.name || t.tenant_name || '';
                const tsub = t.subdomain || '';
                return (
                  <div
                    key={i}
                    className="px-3 py-2 hover:bg-purple-50 dark:hover:bg-purple-900/20 cursor-pointer flex items-center justify-between text-xs border-b border-gray-100 dark:border-gray-600"
                    onClick={() => handleSelectTenant(t)}
                  >
                    <div>
                      <span className="font-semibold text-gray-800 dark:text-white">{tsub}</span>
                      {tname && <span className="text-gray-500 dark:text-gray-400 ml-1">({tname})</span>}
                    </div>
                    <span className="text-gray-400 font-mono text-[10px]">{tid.substring(0, 24)}</span>
                  </div>
                );
              })}
            </div>
          )}
          {showResults && tenantResults.length === 0 && !tenantSearching && (
            <div className="mt-1 p-2 border border-gray-200 dark:border-gray-600 rounded-md text-xs text-gray-400">No tenants found</div>
          )}

          {/* Selected tenant display */}
          {selectedTenant && (
            <div className="mt-2 p-2 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-md">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-xs font-semibold text-purple-800 dark:text-purple-300">{selectedTenant.tenant_name || selectedTenant.tenant_id}</span>
                  {selectedTenant.subdomain && <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">({selectedTenant.subdomain})</span>}
                </div>
                <button type="button" onClick={() => { setSelectedTenant(null); setTenantQuery(''); }} className="text-red-500 hover:text-red-700 text-xs flex items-center gap-0.5">
                  <X size={12} /> Clear
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-0.5">Tenant ID: {selectedTenant.tenant_id}</p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Max Screens</label>
            <input type="number" value={maxScreens} onChange={e => setMaxScreens(Number(e.target.value))} min={1} max={50} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Session TTL (days)</label>
            <input type="number" value={sessionTtl} onChange={e => setSessionTtl(Number(e.target.value))} min={1} max={365} className={inputClass} />
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={() => { resetForm(); onClose(); }} className="px-3 py-1.5 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 text-xs">
            Cancel
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="px-4 py-1.5 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-xs font-medium disabled:opacity-50"
          >
            {createMutation.isPending ? 'Creating...' : 'Create Vendor'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
